import pageConfig from "./caseDetailsConfig.json";
import { getClient } from "./clients";

const activeClientKey = pageConfig.client?.active || "ergobaltic";

const clientConfig = getClient(activeClientKey) || {
  sourceName: "ergobaltic",
  mediaBaseUrl: "https://ergobaltic.dev.media.camcom.ai",
  fallbackEmail: "ergobaltic@camcom.ai",
  loginEndpoint: "/client_login",
  // No source name in the path: userService appends the resolved one, and a
  // literal here would win over it — the same trap ergo.json fell into.
  getUserEndpoint: "/client_user",
};

/**
 * Look up a registered client by its portal-facing source name.
 * Returns null for an unknown key so callers can fall back deliberately.
 */
export const getClientEntry = (key) =>
  getClient(key);

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Merge a client's `tabOverrides` over the shared `tabs` block.
 *
 * The `tabs` block in caseDetailsConfig.json is shared by every client, so a
 * tab that only one client should see (Hegic's Point of Impact and Digital
 * Passport) ships disabled there and is switched on per client instead.
 *
 * Merges recursively, so a single nested leaf can be overridden without
 * restating its siblings — e.g. hiding just the vehicle-information section:
 *
 *   "overview": { "sections": { "vehicleInformation": { "enabled": false } } }
 *
 * A one-level merge would have replaced the whole `sections` object there and
 * silently dropped every other section. Arrays and scalars replace rather than
 * merge, so a client can substitute a whole list (e.g. filter options).
 */
/**
 * Layer one config object over another. Objects merge key by key; arrays and
 * scalars replace, so a client can substitute a whole list.
 */
export const deepMerge = (base, override) => {
  if (!isPlainObject(override)) return override === undefined ? base : override;
  if (!isPlainObject(base)) return { ...override };

  const out = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    out[key] = deepMerge(base[key], value);
  });
  return out;
};

export const applyTabOverrides = (tabs, overrides) =>
  overrides ? deepMerge(tabs, overrides) : tabs;

// sourceName    — portal-facing short name ("bajaj" / "ergo")
// apiSourceName — backend identifier used by assessment API endpoints
//                 Falls back to sourceName if not set (e.g. ergo client)
export const { sourceName, fallbackEmail, loginEndpoint, getUserEndpoint } = clientConfig;
export const apiSourceName = clientConfig.apiSourceName || clientConfig.sourceName;

// apiGeneration — which backend contract this client speaks.
//   "legacy" (default) Bajaj/Ergo: POST, form-encoded, /assessment_new
//   "v2"               Hegic:      GET, query params, /assessment, and errors
//                                  returned as HTTP 200 with an Error field
// See HEGIC_API.md for the full contract.
export const apiGeneration = clientConfig.apiGeneration || "legacy";

export const mediaBaseUrl = clientConfig.mediaBaseUrl || "";

export default clientConfig;

/**
 * Media host for the client the user is logged in as.
 *
 * Resolved at call time, not module load: the client is only known after login,
 * and the build-time `client.active` is the wrong answer for everyone else.
 * Mirrors getApiBaseUrl() in ./urls.
 *
 * Exists because the media host is per-deployment — Hegic's images live on
 * hegicpp.media.camcom.ai, Bajaj's on bagic.media.camcom.ai — and a wrong host
 * fails as a broken image rather than an error, so it is easy to miss.
 */
export const getMediaBaseUrl = () => {
  try {
    const loggedIn = localStorage.getItem("source_name");
    const entry = getClient(loggedIn);
    if (entry?.mediaBaseUrl) return entry.mediaBaseUrl;

    // Server-side config wins over the bundled registry when present.
    const stored = localStorage.getItem("portalConfig");
    if (stored) {
      const fromApi = JSON.parse(stored)?.configuration?.client?.mediaBaseUrl;
      if (fromApi) return fromApi;
    }
  } catch { /* localStorage unavailable or malformed */ }

  return mediaBaseUrl;
};

/**
 * The key prefix this client's media host expects, or "" for the hosts that
 * serve the API's own paths.
 *
 * Same resolution order as getMediaBaseUrl(), and it travels with it: a base
 * URL without its prefix produces a 403 from S3 rather than an image.
 */
export const getMediaPathPrefix = () => {
  try {
    const loggedIn = localStorage.getItem("source_name");
    const entry = getClient(loggedIn);
    if (entry) return entry.mediaPathPrefix || "";

    const stored = localStorage.getItem("portalConfig");
    if (stored) {
      const fromApi = JSON.parse(stored)?.configuration?.client?.mediaPathPrefix;
      if (fromApi) return fromApi;
    }
  } catch { /* localStorage unavailable or malformed */ }
  return "";
};

/**
 * Backend `source_name` for the client the user is logged in as.
 *
 * Same resolution order as getMediaBaseUrl(), and the same reason for existing:
 * the redesign arrived with "ergobaltic" hardcoded in the approve flow, the
 * comment modal and the report download, all of which fail silently for another
 * client — the request succeeds and simply acts on nothing.
 */
export const getApiSourceName = () => {
  try {
    const loggedIn = localStorage.getItem("source_name");
    const entry = getClient(loggedIn);
    if (entry) return entry.apiSourceName || entry.sourceName;

    const stored = localStorage.getItem("portalConfig");
    if (stored) {
      const client = JSON.parse(stored)?.configuration?.client;
      if (client) return client.apiSourceName || client.sourceName;
    }
  } catch { /* localStorage unavailable or malformed */ }

  return apiSourceName;
};
